import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import logo from "../../../assets/digisaka-official-logo.png";

interface VerifyEmailProps {
  email: string;
  onVerified: () => void;
  onBack: () => void;
}

export default function VerifyEmail({ email, onVerified, onBack }: VerifyEmailProps) {
  const navigate = useNavigate();
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const { verifyEmail, resendOTP } = useAuth();

  // Countdown timer for resend button
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (otp.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);

    try {
      await verifyEmail(email, otp);
      try {
        sessionStorage.removeItem('digisaka_pending_signup');
      } catch {
        // ignore
      }
      setSuccess('Email verified successfully! Redirecting to login...');
      setTimeout(() => {
        onVerified();
      }, 2000);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Invalid or expired OTP. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setError('');
    setSuccess('');
    setResendLoading(true);

    try {
      let options:
        | {
            password?: string;
            full_name?: string;
            mobile?: string;
            role?: string;
            sub_role?: string;
            organization?: string;
            agree_policy?: boolean;
            monitoring_level?: 'province' | 'municipality' | 'barangay';
            province?: string;
            province_id?: string;
            municipality?: string;
            city_id?: string;
            barangay?: string;
            barangay_id?: string;
          }
        | undefined;
      try {
        const raw = sessionStorage.getItem('digisaka_pending_signup');
        if (raw) {
          const pending = JSON.parse(raw) as {
            email?: string;
            password?: string;
            full_name?: string;
            mobile?: string;
            role?: string;
            sub_role?: string;
            organization?: string;
            agree_policy?: boolean;
            monitoring_level?: 'province' | 'municipality' | 'barangay';
            province?: string;
            province_id?: string;
            municipality?: string;
            city_id?: string;
            barangay?: string;
            barangay_id?: string;
          };
          if (pending.email === email && pending.password) {
            options = {
              password: pending.password,
              full_name: pending.full_name,
              mobile: pending.mobile,
              role: pending.role,
              sub_role: pending.sub_role,
              organization: pending.organization,
              agree_policy: pending.agree_policy ?? true,
              monitoring_level: pending.monitoring_level,
              province: pending.province,
              province_id: pending.province_id,
              municipality: pending.municipality,
              city_id: pending.city_id,
              barangay: pending.barangay,
              barangay_id: pending.barangay_id,
            };
          }
        }
      } catch {
        // ignore
      }
      if (!options?.password) {
        setError(
          'Session expired for resend. Go back and submit signup again with the same email and password to get a new code.'
        );
        return;
      }
      await resendOTP(email, options);
      setSuccess('A new verification code has been sent to your email (valid for 10 minutes).');
      setResendTimer(60); // Start 60 second cooldown
      setOtp(''); // Clear the OTP input
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to resend code. Please try again.';
      setError(errorMessage);
    } finally {
      setResendLoading(false);
    }
  };

  // Auto-focus on OTP input
  useEffect(() => {
    const otpInput = document.getElementById('otp');
    if (otpInput) {
      otpInput.focus();
    }
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-blue-50 px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        {/* Back to Main Dashboard Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-6"
        >
          <i className="ri-arrow-left-line"></i>
          <span className="text-sm font-medium">Back to Main Dashboard</span>
        </button>

        <div className="text-center mb-8">
          <img src={logo} alt="Digisaka" className="h-12 w-auto mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-gray-900">Verify Your Email</h2>
          <p className="text-gray-600 mt-2">
            We sent a 6-digit verification code to
          </p>
          <p className="text-emerald-600 font-semibold mt-1">{email}</p>
          <p className="text-xs text-gray-500 mt-2">
            The code expires in 10 minutes. After verification you can also sign in with your mobile number.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-2">
              Verification Code
            </label>
            <input
              id="otp"
              type="text"
              value={otp}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                if (value.length <= 6) {
                  setOtp(value);
                }
              }}
              maxLength={6}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition text-center text-2xl tracking-widest font-semibold"
              placeholder="000000"
              autoComplete="off"
            />
            <p className="text-xs text-gray-500 mt-2 text-center">
              Enter the 6-digit code (expires after 10 minutes)
            </p>
          </div>

          <button
            type="submit"
            disabled={loading || otp.length !== 6}
            className="w-full bg-emerald-600 text-white py-3 rounded-lg font-semibold hover:bg-emerald-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Verifying...' : 'Verify Email'}
          </button>
        </form>

        <div className="mt-6 space-y-3">
          <div className="text-center">
            <p className="text-gray-600 text-sm">Didn't receive the code?</p>
            <button
              onClick={handleResendOTP}
              disabled={resendLoading || resendTimer > 0}
              className="text-emerald-600 font-semibold hover:text-emerald-700 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm mt-1"
            >
              {resendLoading
                ? 'Sending...'
                : resendTimer > 0
                ? `Resend in ${resendTimer}s`
                : 'Resend Code'}
            </button>
          </div>

          <div className="text-center pt-4 border-t border-gray-200">
            <button
              onClick={onBack}
              className="text-gray-600 hover:text-gray-800 transition text-sm"
            >
              ← Back to signup
            </button>
          </div>
        </div>

        <div className="mt-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-700 text-center">
            <i className="ri-information-line mr-1"></i>
            The verification code expires in 10 minutes
          </p>
        </div>
      </div>
    </div>
  );
}
